import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment as ENV } from '../../environments/environment';
import { IVaccinationDocument, IVaccinationDocumentEditModel, IVaccinationDocumentStatus } from '../models/VaccinationDocument';
import { Utils } from '../core/Utils';
import { map } from 'rxjs/operators';

/**
 * Vaccination document service
 */
@Injectable({ providedIn: "root" })
export class VaccinationDocumentService {
    constructor(public http: HttpClient) { }

    public get apiUrl(): string { return `${ENV.apiUrl}/vaccinationDocuments` }

    /**
     * Get vaccination documents.
     */
    get(): Observable<IVaccinationDocument[]> {
        return this.http.get<IVaccinationDocument[]>(this.apiUrl);
    }

    /**
     * Get a vaccination document.
     * @param id
     * @returns
     */
    getById(id: number): Observable<IVaccinationDocument> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IVaccinationDocument>(url);
    }

    /**
     * Get vaccination documents owned by the current user.
     * @returns
     */
    getMy(): Observable<IVaccinationDocument[]> {
        const url = `${this.apiUrl}/my`;
        return this.http.get<IVaccinationDocument[]>(url);
    }

    /**
     * Get status of the vaccination documents owned by the current user.
     * @returns
     */
    getMyStatus(): Observable<IVaccinationDocumentStatus[]> {
        const url = `${this.apiUrl}/myStatus`;
        return this.http.get<IVaccinationDocumentStatus[]>(url);
    }

    /**
     * Add a new vaccination document.
     * @param model
     * @returns
     */
    create(model: IVaccinationDocumentEditModel): Observable<number> {
        const form = new FormData();
        const meta = {
            ...model,
            Expires: Utils.getInputDateString(model.Expires),
            File: undefined
        };

        form.append('meta', JSON.stringify(meta));

        if (model.File) {
            form.append('file', model.File, model.File.name);
        }

        return this.http.post<number>(this.apiUrl, form);
    }

    /**
     * Update a vaccination document.
     * @param id
     * @param model
     * @returns
     */
    update(id: number, model: IVaccinationDocumentEditModel): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        const form = new FormData();
        const meta = {
            ...model,
            Expires: Utils.getInputDateString(model.Expires),
            File: undefined
        };

        form.append('meta', JSON.stringify(meta));

        if (model.File) {
            form.append('file', model.File, model.File.name);
        }

        return this.http.put<any>(url, form);
    }

    /**
     * Delete a vaccination document.
     */
    delete(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete<any>(url);
    }

    /**
     * Approve a vaccination document.
     */
    approve(id: number, comment?: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/approve`;
        return this.http.post<any>(url, { comment });
    }

    /**
     * Reject a vaccination document.
     */
    reject(id: number, comment: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/reject`;
        return this.http.post<any>(url, { comment });
    }

    /**
     * Download a vaccination document file.
     * @param id
     * @returns
     */
    download(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/download`;
        return this.http.post(url, null, { responseType: 'blob', observe: 'response' }).pipe(map(response => {
            Utils.downloadResponseFile(response);
            return of(undefined);
        }));
    }
}
